// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import classNames from 'classnames';
import Highlight from '../../Highlight';
import Loading from 'react-loading';

export default class Cell extends React.Component {
    constructor(props) {
        super(props); 

        this.cellRef = React.createRef(); 
    }
    
    state = {
        editing: false, 
        value: '',
        renderMenu: false, 
        loading: false, 
    }

    componentDidMount = () => {
        this.setState({value: this.props.value}); 
    }   

    componentDidUpdate = (prevProps, prevState) => {
        
        // Are we now editing? Add an event listener to check if we click outside fo the input...
        if(this.state.editing && !prevState.editing) {
            document.addEventListener('click', this.handleClickOutside, true);
        }

        // Are we no longer editing? Remove the event listener...
        if(!this.state.editing && prevState.editing) {
            document.removeEventListener('click', this.handleClickOutside, true);
        }
    }

    handleClickOutside = async(e) => {
        if(this.cellRef.current.contains(e.target))
            return; 

        this.cellRef.current.style.paddingRight = '20px';
        this.setState({editing: false, loading: true}); 
        await this.props.onEdit(this.state.value.toString()); 
        this.setState({loading: false}); 
    }

    formatValue = (v) => {
        if(!v) return '-';
        const value = typeof v === 'number'? v? v>0? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }): `(${(v * -1).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`: '-': v;

        return value; 
    }

    renderMenu = () => {
        const items = [...this.props.info??[], ...this.props.errors??[]]
        
        const cell = this.cellRef.current; 
        const parent = cell.parentElement; 

        // Get the bounding box of the parent; 
        const parentX = parent.getBoundingClientRect().x;
        const parentWidth = parent.getBoundingClientRect().width;   

        // Get the bounding box of the cell;
        const cellX = cell.getBoundingClientRect().x; 
        const cellY = cell.getBoundingClientRect().y; 

        const vh = window.innerHeight; 

        // Is the cell at the bottom of the screen? 
        let style = {
            top: null, 
            bottom: null,
            left: null,
            right: null
        }
        if(vh - cellY < 200) {
            style.bottom = '100%'; 
        }else{
            style.top = '100%';
        }
        if(parentX + parentWidth - cellX < 200) {
            style.right = 0;
        }else{
            style.left = 0;
        }

        return (
            <div
                className='cell-context-menu'
                style={{
                    ...style
                }}
            >
                {
                    items.map((error, i) => {
                        return (
                            <div
                                className='cell-context-menu-error-container'
                            >
                                <h3
                                    className='cell-error-title'
                                >
                                    {error.title??'Error'}
                                </h3>
                                <div
                                    className='cell-error-description'
                                >
                                    {error.description??'An error occurred'}
                                </div>
                                <div
                                    className='cell-error-action-container'
                                >
                                    {
                                        error.actions?.map((action, i) => {
                                            return (
                                                <div
                                                    onClick={action.action}
                                                    className='cell-error-action'
                                                >
                                                    {action.label}
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        )
    }

    render = () => {
        return (
            <td
                style={{color: 'inherit', ...this.props.style}}
                onContextMenu={(e) => {
                    if(this.props.onContextMenu)
                        this.props.onContextMenu(e); 
                }}
            >
                <div
                    ref={this.cellRef}
                    className={classNames('spreadsheet-cell', {
                        'cell-low-confidence icon-warn': this.props.errors?.length && (this.props.errors.some(e => e.level === 'low') || this.props.errors.some(e => e.level === 'high')), 
                        'cell-error-low': this.props.errors?.length && this.props.errors.some(e => e.level === 'low'),
                        'cell-error-high': this.props.errors?.length && this.props.errors.some(e => e.level === 'high'),
                        'accepted': this.props.data?.accepted?.[this.props.rowKey.toLowerCase()] === true})}
                    style={{
                        color: 'inherit', 
                        ...this.props.style,
                        position: 'relative',
                        cursor: this.props.canEdit? 'pointer': 'default',
                        float: this.props.style?.alignItems === 'end'? 'right': 'left',
                        height: 35, 
                    }}
                    onClick={(e) => {
                        if(!this.props.canEdit || this.state.editing)
                            return;

                        // Did we click inside of the context menu? return... 
                        if(e.target.closest('.cell-context-menu'))
                            return;

                        // Use the ref, and remove the padding right from the div cell... 
                        this.cellRef.current.style.paddingRight = 0;
                        this.setState({editing: true, renderMenu: false});
                    }}
                    onMouseEnter={() => {

                        if((this.props.errors?.length || this.props.info?.length) && !this.state.editing)
                            this.setState({renderMenu: true})

                        if(!this.props.canEdit)
                            return;
                        
                        if(this.state.loading)
                            return; 

                        this.cellRef.current.style.backgroundImage = 'url("https://static.stax.ai/images/icons/edit.png")';   
                    }}
                    onMouseLeave={() => {
                        this.cellRef.current.style.backgroundImage = '';   
                        this.setState({renderMenu: false})
                    }}
                >
                    {
                        this.state.editing? 
                        <>
                            <input 
                                type='text'
                                style={{
                                    position: 'absolute',
                                    right: -10,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    paddingRight: 30,
                                    margin: 0,
                                    height: '30px',
                                    width: '100px',
                                    zIndex: 1000000
                                }}
                                autoFocus 
                                value={this.state.value}
                                onChange={(e) => this.setState({value: e.target.value})}
                                onKeyDown={async(e) => {
                                    if(e.key === 'Enter') {
                                        this.cellRef.current.style.paddingRight = '20px';
                                        this.setState({editing: false, loading: true});
                                        await this.props.onEdit(this.state.value.toString());
                                        this.setState({loading: false})
                                    }
                                    if(e.key === 'Escape'){
                                        this.cellRef.current.style.paddingRight = '20px';
                                        this.setState({editing: false, value: this.props.value});
                                    }
                                }}
                            />
                            <div 
                                style={{
                                    width: 30,
                                    height: 30, 
                                    position: 'absolute',
                                    right: -9, 
                                    backgroundImage: 'url("https://static.stax.ai/images/icons/confirm.png")',
                                    backgroundSize: 15,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    zIndex: 1000001
                                }}
                                onClick={async() => {
                                    this.cellRef.current.style.paddingRight = '20px';
                                    this.setState({editing: false, value: this.state.value??0});
                                    this.setState({loading: true})
                                    await this.props.onEdit(this.state.value.toString());
                                    this.setState({loading: false})
                                }}
                            />
                        </>:
                        <>
                            { this.state.loading ?
                            <>
                                <Highlight text={this.formatValue(this.props.value)} match={this.props.query} />
                                <div 
                                    style={{
                                        width: 30,
                                        height: 30, 
                                        position: 'absolute',
                                        top: 5, 
                                        right: -15, 
                                        zIndex: 1000001
                                    }}>
                                    <Loading type='spin'
                                        color="var(--color-primary)"
                                        height={15}
                                        width={15} /> 
                                </div>
                            </>: 
                            <Highlight text={this.formatValue(this.props.value)} match={this.props.query} />  }
                        </>
                    }
                    {
                    this.state.renderMenu?
                    this.renderMenu()
                    :""
                }
                </div>
             
            </td>
        )
    }

}